import { getSizeType } from '../helpers/universalSizeMapping'

const FILTERS_BLACKLIST = [
]

const RANGE_INDICATOR = '<->'

const isSizeFilterField = (field) => Boolean(getSizeType(field))

const PRICE_FILTER_FIELDS = ['price', 'ss_price_range', 'ss_price']

const isPriceFilterField = (field) => PRICE_FILTER_FIELDS.includes(field)

const TOGGLE_FILTERS = {
  mfield_products_online_only: {
    onValue: 'true',
    label: 'Exclude Online Only'
  }
}

const isToggleFilterField = (field) => Boolean(TOGGLE_FILTERS[field])

const getToggleFilterConfig = (field) => TOGGLE_FILTERS[field] || null

const getToggleFilterValue = (field) => {
  const config = TOGGLE_FILTERS[field]
  if (!config) return null
  return `${field}:${config.onValue}`
}

const isToggleFilterActive = (field, activeFilters) => {
  if (!field || !activeFilters) return false
  for (const item of activeFilters) {
    if (item.startsWith(`${field}:`)) return true
  }
  return false
}

const parseFiltersFromResults = (results) => {
  if (!results?.facets) return { fetchedFilters: [], sizeFilters: [], displayOrder: [] }
  const whiteListedFilters = results.facets.filter((facet) => {
    return !FILTERS_BLACKLIST.some((field) => field === facet.field)
  })
  const cleanedFilters = whiteListedFilters.map((facet) => ({
    ...facet,
    values: explodeFacetValues(facet.field, facet.values)
  }))
  const sizeFilters = cleanedFilters.filter((filter) => isSizeFilterField(filter.field))
  const otherFilters = cleanedFilters.filter((filter) => !isSizeFilterField(filter.field))

  const displayOrder = []
  let sizeInserted = false
  for (const filter of cleanedFilters) {
    if (isSizeFilterField(filter.field)) {
      if (!sizeInserted) {
        displayOrder.push({ type: 'size' })
        sizeInserted = true
      }
      continue
    }
    displayOrder.push({ type: 'filter', field: filter.field })
  }

  return { fetchedFilters: otherFilters, sizeFilters, displayOrder }
}

function parseFilterList(value) {
  if (!value) return []
  if (value instanceof Set) return [...value]
  if (Array.isArray(value)) return value
  if (typeof value !== 'string') return []

  const filterArray = []
  const parts = value.split(',')
  let currentFilter = null

  for (const part of parts) {
    if (!part) continue

    const colonIndex = part.indexOf(':')
    if (colonIndex > 0 && colonIndex < part.length - 1) {
      if (currentFilter) filterArray.push(currentFilter)
      currentFilter = part
    } else if (currentFilter) {
      currentFilter += ',' + part
    }
  }

  if (currentFilter) filterArray.push(currentFilter)
  return filterArray
}

function splitFilterPair(filter) {
  if (!filter || typeof filter !== 'string') return { field: '', value: '' }
  const colonIndex = filter.indexOf(':')
  if (colonIndex < 0) return { field: filter, value: '' }
  return {
    field: filter.slice(0, colonIndex),
    value: filter.slice(colonIndex + 1)
  }
}

const FILTER_VALUE_ENCODINGS = new Map()

function encodingKey(field, term) {
  return `${field}::${String(term ?? '').trim().toLowerCase()}`
}

function decodeFilterText(raw) {
  return String(raw ?? '')
    .trim()
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
}

function tryParseJSON(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || !(trimmed.startsWith('[') || trimmed.startsWith('{') || trimmed.startsWith('"'))) return null
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

function isEmptyFilterValue(raw) {
  const text = String(raw ?? '').trim()
  return !text || text === '"'
}

function isJsonFragmentValue(raw) {
  const text = decodeFilterText(raw)
  if (!text) return false
  if (tryParseJSON(text) != null) return false
  return /[[\]]/.test(text)
}

function registerFilterValueEncoding(field, term, raw) {
  if (!field || !term || raw == null || raw === '') return
  const key = encodingKey(field, term)
  if (!FILTER_VALUE_ENCODINGS.has(key)) FILTER_VALUE_ENCODINGS.set(key, new Set())
  FILTER_VALUE_ENCODINGS.get(key).add(String(raw))
}

function parseFilterValueTerms(raw, depth = 0) {
  if (raw == null || depth > 5) return []
  if (isEmptyFilterValue(raw)) return []

  const text = decodeFilterText(raw)
  if (!text) return []

  const parsed = tryParseJSON(text)
  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) => parseFilterValueTerms(item, depth + 1))
  }
  if (typeof parsed === 'string') {
    return parseFilterValueTerms(parsed, depth + 1)
  }
  if (parsed != null && typeof parsed !== 'object') {
    return [String(parsed)]
  }

  if (text.includes('•')) {
    return text.split('•').flatMap((part) => parseFilterValueTerms(part, depth + 1))
  }

  const stripped = text.replace(/^[\s[\]"']+|[\s[\]"']+$/g, '').trim()
  if (!stripped) return []
  if (stripped !== text) return parseFilterValueTerms(stripped, depth + 1)
  if (isEmptyFilterValue(stripped)) return []

  return [stripped]
}

function formatFilterDisplayValue(raw) {
  return parseFilterValueTerms(raw).join(', ')
}

function explodeFacetValues(field, values) {
  const ranges = []
  const byTerm = new Map()

  for (const item of values || []) {
    if (item?.type === 'range' || item?.type === 'slider') {
      ranges.push(item)
      continue
    }

    const raw = item?.value ?? item?.label
    const terms = parseFilterValueTerms(raw)
    if (!terms.length) continue

    for (const term of terms) {
      registerFilterValueEncoding(field, term, raw)
      const key = term.toLowerCase()
      const existing = byTerm.get(key)
      const count = item.count || 0

      if (!existing) {
        byTerm.set(key, {
          ...item,
          value: term,
          label: term,
          count
        })
        continue
      }

      existing.count = (existing.count || 0) + count
      if (!isJsonFragmentValue(raw) && isJsonFragmentValue(existing.value)) {
        existing.value = term
        existing.label = term
      }
    }
  }

  return [...ranges, ...byTerm.values()]
}

function getFilterValueEncodings(field, value) {
  const term = parseFilterValueTerms(value)[0] || value
  const registered = FILTER_VALUE_ENCODINGS.get(encodingKey(field, term))
  const encodings = new Set()
  if (value) encodings.add(String(value))
  if (term) {
    encodings.add(String(term))
    encodings.add(JSON.stringify([term]))
  }
  if (registered) registered.forEach((item) => encodings.add(item))
  return [...encodings]
}

function getRelatedFilterKeys(field, value) {
  return getFilterValueEncodings(field, value).map((raw) => `${field}:${raw}`)
}

function valueKey(item) {
  return String(item?.value ?? item?.label ?? '')
}

function mergeFilterSnapshots(current, next, replace = false) {
  if (replace) return next?.length ? next : current || []
  if (!current?.length) return next || []
  if (!next?.length) return current

  const byField = new Map(
    current.map((filter) => [
      filter.field,
      {
        ...filter,
        values: [...(filter.values || [])]
      }
    ])
  )

  for (const filter of next) {
    const existing = byField.get(filter.field)
    if (!existing) {
      byField.set(filter.field, filter)
      continue
    }

    const seen = new Set(existing.values.map(valueKey))
    for (const item of filter.values || []) {
      const key = valueKey(item)
      if (!key || seen.has(key)) continue
      existing.values.push(item)
      seen.add(key)
    }
  }

  const order = [...next.map((filter) => filter.field), ...current.map((filter) => filter.field)]
  return [...new Set(order)].map((field) => byField.get(field)).filter(Boolean)
}

function parseActiveRange(field, activeFilters) {
  if (!field || !activeFilters) return null

  for (const activeFilter of activeFilters) {
    if (!activeFilter.startsWith(`${field}:`)) continue

    const value = activeFilter.slice(field.length + 1)
    if (!value.includes(RANGE_INDICATOR)) continue

    const [low, high] = value.split(RANGE_INDICATOR).map(parseFloat)
    if (isNaN(low) || isNaN(high)) continue

    return [low, high]
  }

  return null
}

export {
  parseFiltersFromResults,
  parseFilterList,
  splitFilterPair,
  parseActiveRange,
  PRICE_FILTER_FIELDS,
  isPriceFilterField,
  isSizeFilterField,
  isToggleFilterField,
  getToggleFilterConfig,
  getToggleFilterValue,
  isToggleFilterActive,
  formatFilterDisplayValue,
  explodeFacetValues,
  parseFilterValueTerms,
  getFilterValueEncodings,
  getRelatedFilterKeys,
  mergeFilterSnapshots,
  RANGE_INDICATOR
}
